import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './icons/Icon';
import { DATE_RANGES, useDashboardFilters } from '../contexts/DashboardFiltersContext';
import type { NavSection, NavTab } from '../config/navigation';

interface AdminData {
  id: string;
  username: string;
  email: string;
  role: string;
}

interface SuperAdminHeaderProps {
  adminData: AdminData | null;
  onLogout: () => void;
  section?: NavSection;
  tab?: NavTab;
  onOpenSearch?: () => void;
  onToggleSidebar?: () => void;
  onOpenMobileNav?: () => void;
  isSidebarCollapsed?: boolean;
}

/**
 * Top bar.
 *
 * The old header was 80px of brand: a 48px logo, a wordmark, the tagline
 * "Chill Responsibly", a badge, a greeting, an avatar, and two buttons — none
 * of which told the operator where they were or let them filter anything. The
 * brand now lives once, in the sidebar; this row carries the things a console
 * header is for: location (breadcrumb), the global date range every panel
 * reads, search, and the account menu.
 */
const SuperAdminHeader: React.FC<SuperAdminHeaderProps> = ({
  adminData,
  onLogout,
  section,
  tab,
  onOpenSearch,
  onToggleSidebar,
  onOpenMobileNav,
  isSidebarCollapsed = false,
}) => {
  const { range, setRange } = useDashboardFilters();
  const [isRangeOpen, setIsRangeOpen] = useState(false);
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const rangeRef = useRef<HTMLDivElement>(null);
  const accountRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target as Node)) setIsRangeOpen(false);
      if (accountRef.current && !accountRef.current.contains(event.target as Node)) setIsAccountOpen(false);
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsRangeOpen(false);
        setIsAccountOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const initial = adminData?.username?.charAt(0)?.toUpperCase() || 'A';

  return (
    <header className="sticky top-0 z-20 flex h-topbar items-center gap-2 border-b border-border-subtle bg-surface/95 px-3 backdrop-blur-sm sm:px-4">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="rounded-md p-1.5 text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue lg:hidden"
      >
        <Icon name="menu" className="h-5 w-5" title="Open navigation" />
      </button>

      <button
        type="button"
        onClick={onToggleSidebar}
        className="hidden rounded-md p-1.5 text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue lg:block"
      >
        <Icon
          name={isSidebarCollapsed ? 'expand' : 'collapse'}
          className="h-5 w-5"
          title={isSidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'}
        />
      </button>

      {/* Breadcrumb: the console is two levels deep and previously said so
          nowhere — the only cue was which tab happened to be underlined. */}
      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex items-center gap-1.5 text-sm">
          <li className="hidden shrink-0 text-ink-3 sm:block">Super Admin</li>
          <li aria-hidden="true" className="hidden text-ink-3 sm:block">
            <Icon name="chevronRight" className="h-3.5 w-3.5" />
          </li>
          <li className="hidden min-w-0 shrink text-ink-2 md:block">
            <span className="truncate">{section?.label ?? 'Console'}</span>
          </li>
          {tab ? (
            <>
              <li aria-hidden="true" className="hidden text-ink-3 md:block">
                <Icon name="chevronRight" className="h-3.5 w-3.5" />
              </li>
              <li className="min-w-0">
                <span className="block truncate font-semibold text-ink" aria-current="page">
                  {tab.label}
                </span>
              </li>
            </>
          ) : null}
        </ol>
      </nav>

      <button
        type="button"
        onClick={onOpenSearch}
        className="flex items-center gap-2 rounded-lg border border-border-default px-2.5 py-1.5 text-sm text-ink-3 transition-colors hover:bg-surface-hover hover:text-ink-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
      >
        <Icon name="search" className="h-4 w-4" />
        <span className="hidden lg:inline">Jump to…</span>
        <kbd className="hidden rounded border border-border-default px-1 py-px text-[10px] font-semibold lg:inline">⌘K</kbd>
        <span className="sr-only lg:hidden">Search the console</span>
      </button>

      {/* Global date range. One control, read by every panel that supports it,
          rather than a per-widget picker in each. */}
      <div ref={rangeRef} className="relative">
        <button
          type="button"
          onClick={() => setIsRangeOpen((open) => !open)}
          aria-haspopup="listbox"
          aria-expanded={isRangeOpen}
          className="flex items-center gap-1.5 rounded-lg border border-border-default px-2.5 py-1.5 text-sm font-medium text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        >
          <Icon name="calendar" className="h-4 w-4 text-ink-3" />
          <span className="hidden whitespace-nowrap sm:inline">{range.label}</span>
          <span className="sr-only sm:hidden">Date range: {range.label}</span>
          <Icon name="chevronDown" className="h-3.5 w-3.5 text-ink-3" />
        </button>
        {isRangeOpen ? (
          <ul
            role="listbox"
            aria-label="Date range"
            className="absolute right-0 z-30 mt-1.5 w-48 overflow-hidden rounded-lg border border-border-default bg-surface py-1 shadow-popover"
          >
            {DATE_RANGES.map((option) => (
              <li key={option.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.key === range.key}
                  onClick={() => {
                    setRange(option.key);
                    setIsRangeOpen(false);
                  }}
                  className={`block w-full px-3 py-2 text-left text-sm transition-colors hover:bg-surface-hover ${
                    option.key === range.key ? 'font-semibold text-brand-blue' : 'text-ink-2'
                  }`}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div ref={accountRef} className="relative">
        <button
          type="button"
          onClick={() => setIsAccountOpen((open) => !open)}
          aria-haspopup="menu"
          aria-expanded={isAccountOpen}
          className="flex items-center gap-1.5 rounded-lg p-0.5 pr-1.5 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-blue text-xs font-bold text-white">
            {initial}
          </span>
          <span className="sr-only">Account menu for {adminData?.username ?? 'admin'}</span>
          <Icon name="chevronDown" className="h-3.5 w-3.5 text-ink-3" />
        </button>
        {isAccountOpen ? (
          <div
            role="menu"
            className="absolute right-0 z-30 mt-1.5 w-56 overflow-hidden rounded-lg border border-border-default bg-surface py-1 shadow-popover"
          >
            <div className="border-b border-border-subtle px-3 py-2.5">
              <p className="truncate text-sm font-semibold text-ink">{adminData?.username ?? 'Admin'}</p>
              <p className="truncate text-xs text-ink-3">{adminData?.email ?? ''}</p>
              {/* The actual role rather than a hardcoded "Super Admin". There
                  is one role now, and printing a tier that no longer exists
                  invites someone to look for the other one. */}
              <span className="mt-1.5 inline-block rounded-full bg-info-bg px-2 py-0.5 text-[11px] font-semibold text-brand-blue">
                {adminData?.role === 'admin' ? 'Admin' : (adminData?.role ?? 'Admin')}
              </span>
            </div>
            {/* The "Admin Dashboard" link that used to sit here pointed at
                /admin, a second console with its own login. That page is gone
                and /admin now redirects here, so the link led back to the page
                you were already on. */}
            <Link
              to="/"
              role="menuitem"
              className="flex items-center gap-2 px-3 py-2 text-sm text-ink-2 transition-colors hover:bg-surface-hover"
            >
              <Icon name="external" className="h-4 w-4 text-ink-3" />
              Storefront
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={onLogout}
              className="flex w-full items-center gap-2 border-t border-border-subtle px-3 py-2 text-left text-sm font-semibold text-negative transition-colors hover:bg-negative-bg"
            >
              <Icon name="logout" className="h-4 w-4" />
              Log out
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default SuperAdminHeader;
